import { getTailwindColor } from '@/utils/colors';
import clsx from 'clsx';

type ListType = 'decimal' | 'upper-alpha' | 'lower-alpha' | 'upper-roman' | 'lower-roman';

type NestedListBlock = {
    blockName: 'core/list';
    attrs: {
        ordered?: boolean;
        type?: ListType;
        reversed?: boolean;
        start?: number;
    };
    innerHTML: string;
    innerContent: (string | null)[];
    innerBlocks: ListItemBlock[];
};

type ListItemBlock = {
    blockName: 'core/list-item';
    attrs: Record<string, unknown>;
    innerHTML: string;
    innerContent: (string | null)[];
    innerBlocks: NestedListBlock[];
};

export type ListProps = {
    content: ListItemBlock[];
    index: number;
    hash: string;
    ordered?: boolean;
    useDottedUnorderedList?: boolean;
    useStandardStyle?: boolean;
    useCheckMarkStyle?: boolean;
    useCircularCheckMarkStyle?: boolean;
    useLargeNumbersStyle?: boolean;
    textColor?: string;
    unorderedListColor?:
        | {
              color: string;
              name: string;
          }
        | string;
    backgroundColor?: string;
    start?: number;
    reversed?: boolean;
    type?: ListType;
};

const listTypeMap: Record<ListType, '1' | 'a' | 'A' | 'i' | 'I'> = {
    decimal: '1',
    'lower-alpha': 'a',
    'upper-alpha': 'A',
    'lower-roman': 'i',
    'upper-roman': 'I',
};

const listStyleClassMap: Record<ListType, string> = {
    decimal: 'r-list-decimal',
    'lower-alpha': 'r-list-[lower-alpha]',
    'upper-alpha': 'r-list-[upper-alpha]',
    'lower-roman': 'r-list-[lower-roman]',
    'upper-roman': 'r-list-[upper-roman]',
};

const getListStyleClass = (ordered?: boolean, type?: ListType): string => {
    if (!ordered) return 'r-list-disc';
    if (!type) return 'r-list-decimal';
    return listStyleClassMap[type] ?? 'r-list-decimal';
};

export const List = ({ content, ...props }: ListProps) => {
    // Backwards compat: If you resave a page, this will be updated
    // to the new markup where content is an array of <li />
    if (typeof content === 'string') {
        return <div dangerouslySetInnerHTML={{ __html: content }} />;
    }

    if (!Array.isArray(content)) {
        return null;
    }

    const ListTag = props.ordered || props.useLargeNumbersStyle ? 'ol' : 'ul';
    const classNames = clsx(
        'styled-link r-relative r-mb-4',
        getTailwindColor(props.textColor, 'text'),
        getTailwindColor(props.backgroundColor, 'bg'),
        getTailwindColor(props.unorderedListColor || 'black', 'border'),
        props.ordered && !props.useLargeNumbersStyle && getListStyleClass(true, props.type),
        {
            'r-border-l-4 r-pl-8 r-ml-8':
                !props.useStandardStyle &&
                !props.useDottedUnorderedList &&
                !props.useCheckMarkStyle &&
                !props.useCircularCheckMarkStyle &&
                !props.useLargeNumbersStyle,
            'r-list-disc r-pl-4': props.useStandardStyle && !props.ordered,
            'r-list-image-none': (props.useCheckMarkStyle || props.useCircularCheckMarkStyle) && (!props.ordered || props.useLargeNumbersStyle),
            'r-pl-4': props.ordered && !props.useLargeNumbersStyle,
        }
    );

    const renderNestedList = (innerBlocks: NestedListBlock[]) => {
        if (innerBlocks.length === 0) {
            return null;
        }

        const nestedList = innerBlocks.find(block => block.blockName === 'core/list');
        if (!nestedList || nestedList.innerBlocks.length === 0) {
            return null;
        }

        const { ordered, type, start, reversed } = nestedList.attrs;
        const NestedListTag = ordered ? 'ol' : 'ul';
        const nestedHtmlType = type ? listTypeMap[type] : undefined;

        const nestedClassName = clsx('r-mt-1 r-pl-6', getListStyleClass(ordered, type));

        return (
            <NestedListTag
                className={nestedClassName}
                {...(nestedHtmlType && { type: nestedHtmlType })}
                {...(start !== undefined && { start })}
                {...(reversed && { reversed: true })}
            >
                {nestedList.innerBlocks.map((item, idx) => {
                    const matches = item.innerHTML.match(/<li\b[^>]*>([\s\S]*?)<\/li>/);
                    if (!Array.isArray(matches) || !matches[1]) {
                        return null;
                    }

                    return (
                        <li key={idx} className="r-pb-1">
                            <span dangerouslySetInnerHTML={{ __html: matches[1] }} />
                            {item.innerBlocks.length > 0 && renderNestedList(item.innerBlocks)}
                        </li>
                    );
                })}
            </NestedListTag>
        );
    };

    const htmlType = props.type ? listTypeMap[props.type] : undefined;

    return (
        <ListTag
            className={classNames}
            {...(props.start !== undefined && { start: props.start })}
            {...(props.reversed && { reversed: true })}
            {...(htmlType && { type: htmlType })}
        >
            {content.map(({ innerHTML, innerBlocks }, index) => {
                const matches = innerHTML.match(/<li\b[^>]*>([\s\S]*?)<\/li>/);
                if (!Array.isArray(matches) || !matches[1]) {
                    return null;
                }

                const hasCustomStyle = !props.useStandardStyle && !props.ordered;

                return (
                    <li
                        key={index}
                        className={clsx({
                            'r-pb-2': (props.useCheckMarkStyle || props.useCircularCheckMarkStyle) && !props.ordered,
                        })}
                    >
                        <div
                            className={clsx({
                                'r-flex r-items-start r-gap-3': hasCustomStyle,
                                'r-flex r-items-start r-gap-3 r-py-6 r-pl-0': props.useLargeNumbersStyle,
                            })}
                        >
                            {props.useDottedUnorderedList && !props.ordered ? (
                                <span
                                    className={clsx(
                                        'r-flex r-shrink-0 r-items-center',
                                        props.unorderedListColor ? getTailwindColor(props.unorderedListColor, 'text') : ''
                                    )}
                                    style={{ height: '1lh' }}
                                >
                                    &mdash;
                                </span>
                            ) : null}
                            {props.useCheckMarkStyle && !props.ordered ? (
                                <span className="r-flex r-shrink-0 r-items-center" style={{ height: '1lh' }}>
                                    <svg
                                        width="22"
                                        height="16"
                                        viewBox="0 0 22 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        aria-hidden="true"
                                        focusable="false"
                                    >
                                        <path
                                            className={clsx(getTailwindColor(props.unorderedListColor || 'listCheck', 'fill'))}
                                            d="M21.5 2.0564C21.5 2.44021 21.3535 2.82421 21.0605 3.11671L9.06055 15.1167C8.76875 15.4111 8.38438 15.5564 8 15.5564C7.61562 15.5564 7.23219 15.4099 6.93969 15.1169L0.939688 9.11694C0.646719 8.82515 0.5 8.44077 0.5 8.0564C0.5 7.19952 1.20078 6.5564 2 6.5564C2.38381 6.5564 2.76781 6.70288 3.06031 6.99585L8 11.9376L18.9406 0.997022C19.2312 0.703115 19.6156 0.556396 20 0.556396C20.8016 0.556396 21.5 1.19858 21.5 2.0564Z"
                                        />
                                    </svg>
                                </span>
                            ) : null}
                            {props.useCircularCheckMarkStyle && !props.ordered ? (
                                <span className="r-flex r-shrink-0 r-items-center" style={{ height: '1lh' }}>
                                    <svg
                                        className="r-size-8"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 48 48"
                                        width="48"
                                        height="48"
                                        aria-hidden="true"
                                        focusable="false"
                                    >
                                        <circle
                                            cx="24"
                                            cy="24"
                                            r="18"
                                            fill="transparent"
                                            className={getTailwindColor(props.unorderedListColor || 'listCheck', 'stroke') || ''}
                                            strokeWidth="3"
                                        />
                                        <path
                                            className={getTailwindColor(props.unorderedListColor || 'listCheck', 'stroke') || ''}
                                            d="M16 24.5 L22 30 L34 18"
                                            fill="none"
                                            strokeWidth="3.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                            ) : null}
                            {props.useLargeNumbersStyle && (
                                <span
                                    className={clsx(
                                        '@container/num -r-my-2 r-flex r-items-center r-justify-start r-font-semibold r-leading-none',
                                        getTailwindColor(props.unorderedListColor || 'primary', 'text') || ''
                                    )}
                                    style={{ containerType: 'size', width: `${String(content.length).length}ch`, height: '4lh' }}
                                >
                                    <span style={{ fontSize: '100cqh' }}>
                                        {props.reversed ? (props.start ?? content.length) - index : (props.start ?? 1) + index}
                                    </span>
                                </span>
                            )}
                            <span className={props.useLargeNumbersStyle ? 'r-pl-10' : ''} dangerouslySetInnerHTML={{ __html: matches[1] }} />
                        </div>
                        {innerBlocks && innerBlocks.length > 0 && (
                            <div className={clsx({ 'r-ml-8': hasCustomStyle })}>{renderNestedList(innerBlocks)}</div>
                        )}
                    </li>
                );
            })}
        </ListTag>
    );
};
